/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/build/useQaNewChecklistDraft.ts
 * FILE_ID: JOBHUB-QA-NEWCL-DRAFT-HOOK-001
 * PURPOSE: Draft rows + Save for a new checklist; the table and card layouts share it.
 * LANDMINE: nothing is written until save(). Save writes sequentially so item
 *      numbers stay in draft order; rows without a title are skipped.
 */
import { useState } from 'react'
import type { QaApi } from '../../../qa/qaApi'

export type QaDraftRow = {
  key: string
  title: string
  product: string
  requirement_text: string
  acceptance_criteria: string
  has_hold_point: boolean
}

export function blankDraftRow(): QaDraftRow {
  return {
    key: `draft-${Math.random().toString(36).slice(2, 8)}`,
    title: '',
    product: '',
    requirement_text: '',
    acceptance_criteria: '',
    has_hold_point: false,
  }
}

export function useQaNewChecklistDraft({
  areaId,
  api,
  onCreated,
  onMutated,
}: {
  areaId: string
  api: QaApi
  onCreated: (checklistId: string) => void
  onMutated: () => void
}) {
  const [title, setTitle] = useState('New checklist')
  const [rows, setRows] = useState<QaDraftRow[]>([blankDraftRow()])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const patch = (key: string, fields: Partial<QaDraftRow>) => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...fields } : row)))
  }

  const move = (index: number, direction: -1 | 1) => {
    const next = index + direction
    if (next < 0 || next >= rows.length) return
    setRows((current) => {
      const copy = current.slice()
      const [row] = copy.splice(index, 1)
      copy.splice(next, 0, row)
      return copy
    })
  }

  const addRow = () => setRows((current) => [...current, blankDraftRow()])
  const removeRow = (key: string) =>
    setRows((current) => current.filter((entry) => entry.key !== key))

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const created = await api.addChecklist(areaId, title.trim() || 'New checklist')
      for (const row of rows) {
        if (!row.title.trim()) continue
        await api.addItem(areaId, {
          title: row.title,
          product: row.product,
          requirement_text: row.requirement_text,
          acceptance_criteria: row.acceptance_criteria,
          has_hold_point: row.has_hold_point,
        }, null, null, created.id)
      }
      onCreated(created.id)
      onMutated()
    } catch (err) {
      const body = (err as { response?: { data?: { error?: string; detail?: string } } })?.response?.data
      setError(body?.error || body?.detail || 'Could not create the checklist.')
    } finally {
      setBusy(false)
    }
  }

  return { title, setTitle, rows, busy, error, patch, move, addRow, removeRow, save }
}
